import { Link } from 'react-router-dom'
import {
  Sparkles,
  FileSearch,
  Layers,
  ShieldCheck,
  MessageSquare,
  GitBranch,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react'
import { useSeo } from '@/lib/useSeo'
import { PageHeader } from '@/components/public/PageHeader'

export function Awa() {
  useSeo({
    title: 'AI Workflow Automation',
    description:
      'AI Workflow Automation (AWA) is the engine inside VICE. It extracts, validates, and explains entry data so officers can focus on judgment. Built on MetaPhase ChallengeAI.',
  })

  return (
    <>
      <PageHeader
        eyebrow="AI Workflow Automation"
        title="AWA does the repetitive work. Agents make the calls."
        lede="AWA is the AI engine inside VICE. It reads the document, pulls the line items, cross checks them against the rules, and provides the agent clear actionable results ready for review."
      >
        <div className="mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-200">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-white/10 backdrop-blur">
            <Sparkles size={12} aria-hidden="true" className="text-accent-sunset" />
            Powered by ChallengeAI
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-white/10 backdrop-blur">
            <CheckCircle2 size={12} aria-hidden="true" className="text-accent-turquoise" />
            Awardable in Tradewinds Solutions Marketplace
          </span>
        </div>
      </PageHeader>

      {/* ── Why AI ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-3xl">
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            Why AI
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
            Customs data is repetitive. Judgment is not.
          </h2>
          <p className="mt-4 text-lg text-neutral-700 leading-relaxed">
            A simple entry summary takes 10 to 30 minutes to review today. A complex one pulls
            7 to 9 people into the loop. Most of that time is spent transcribing, comparing, and
            catching inconsistencies, exactly the work large language models and modern text
            extraction do well.
          </p>
          <p className="mt-4 text-lg text-neutral-700 leading-relaxed">
            VICE doesn&apos;t replace the USVI Agent&apos;s judgment. It clears the desk so the
            agent can use it.
          </p>
        </div>
      </section>

      {/* ── Pipeline ── */}
      <section className="bg-secondary-lightest py-20 border-y border-secondary-lighter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
              How AWA works
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
              Six steps from document to decision.
            </h2>
          </div>
          <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                Icon: FileSearch,
                title: 'Ingest',
                body: 'Accepts the digital formats already used to prepare entry data for submission.',
              },
              {
                Icon: Sparkles,
                title: 'Extract',
                body: 'Text extraction and language model reading pull structured fields from the document with per span confidence scoring.',
              },
              {
                Icon: Layers,
                title: 'Bucket',
                body: 'Extracted data is organized into entry details, including shipment, parties, classification, valuation, and duties.',
              },
              {
                Icon: ShieldCheck,
                title: 'Validate',
                body: 'HTS codes, entry types, and values are cross-checked against reference data and past entries.',
              },
              {
                Icon: MessageSquare,
                title: 'Explain',
                body: 'A chat assistant explains flags and codes without taking the agent out of the review surface.',
              },
              {
                Icon: GitBranch,
                title: 'Hand off',
                body: 'A focused review list surfaces flagged items first. The agent confirms, edits, or rejects.',
              },
            ].map(({ Icon, title, body }, idx) => (
              <li
                key={title}
                className="relative bg-white rounded-lg p-6 border border-neutral-200 shadow-sm"
              >
                <div className="absolute top-4 right-4 text-[11px] font-mono font-bold text-neutral-300">
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <div className="inline-flex p-3 rounded bg-secondary-lightest text-secondary">
                  <Icon size={22} aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-primary">{title}</h3>
                <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            What it means
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-primary">
            Faster reviews, better data, auditable decisions.
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              stat: 'Minutes, not hours',
              body: 'AWA prepares a reviewable record before the agent opens the file. The review is focused on the flags, not the form.',
            },
            {
              stat: 'Line-level data',
              body: 'For the first time, USVI entry data reaches ACE at line level, giving risk and revenue teams the detail they need.',
            },
            {
              stat: 'Every Risk or Flag is Traceable',
              body: 'Every result is traceable, with source data, confidence, and reasoning available for review and audit.',
            },
            {
              stat: 'Humans in the loop',
              body: 'AWA provides recommendations. The officer makes the final decision. No submission is sent to ACE without confirmation.',
            },
            {
              stat: 'Explainable by default',
              body: 'Each flag includes the rule, source data, or condition that led to the recommendation.',
            },
            {
              stat: 'Continuous improvement',
              body: 'Feedback from agent corrections tunes the validation rules. Tomorrow\'s extractions learn from today\'s reviews.',
            },
          ].map(({ stat, body }) => (
            <article
              key={stat}
              className="bg-white rounded-lg border border-neutral-200 p-6 shadow-sm"
            >
              <div className="text-lg font-bold text-primary">{stat}</div>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── ChallengeAI + Tradewinds ── */}
      <section className="bg-primary text-white py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-center bg-cover"
          style={{ backgroundImage: "url('/images/port-sunset.jpg')" }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(10,34,64,0.95) 0%, rgba(10,34,64,0.85) 60%, rgba(46,196,199,0.25) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-sm font-semibold uppercase tracking-wider text-accent-sunset">
            Built on ChallengeAI
          </div>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl">
            AWA rides on{' '}
            <a
              href="https://challengeai.metaphase.tech"
              target="_blank"
              rel="noreferrer"
              className="text-accent-sunset hover:text-accent-coral"
            >
              ChallengeAI
            </a>
            , MetaPhase&apos;s AI accelerator.
          </h2>
          <div className="mt-8 grid gap-8 lg:grid-cols-3 items-start">
            <div className="lg:col-span-2 space-y-4">
              <p className="text-neutral-200 leading-relaxed">
                <strong className="text-white">ChallengeAI</strong> is MetaPhase&apos;s accelerator
                suite for moving AI-assisted workflows from prompt to production. It replaces
                manual, document-driven processes with automated, compliant pipelines that span
                design, development, testing, security, deployment, and operations.
              </p>
              <p className="text-neutral-200 leading-relaxed">
                ChallengeAI embeds security and operational controls directly into the delivery
                pipeline, so AWA ships with audit, compliance, and observability baked in — not
                bolted on.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded bg-accent-sunset text-primary font-bold hover:bg-accent-sunset/90 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
                >
                  Talk to us about AWA
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
                <a
                  href="https://www.tradewindai.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded border border-white/30 font-semibold hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2"
                >
                  Visit Tradewinds
                </a>
              </div>
            </div>
            <a
              href="https://www.tradewindai.com/"
              target="_blank"
              rel="noreferrer"
              aria-label="Tradewinds Solutions Marketplace — Awardable"
              className="block rounded-lg bg-white/5 backdrop-blur p-6 border border-white/15 hover:bg-white/10 transition focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-4"
            >
              <img
                src="/images/tradewinds-awardable.png"
                alt=""
                className="block mx-auto h-40 w-auto"
              />
              <p className="mt-4 text-center text-xs text-neutral-200 leading-relaxed">
                <CheckCircle2
                  size={12}
                  aria-hidden="true"
                  className="inline mr-1 -mt-0.5 text-accent-turquoise"
                />
                Assessed <strong className="text-white">Awardable</strong> in the U.S. Department
                of War CDAO Tradewinds Solutions Marketplace.
              </p>
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
